// `(limit ds N)` — row-count cap. Top-level eager op consumable as
// `(limit ds 5)` directly or via `(-> ds (limit 5))`.

import { z } from 'zod';

import { opMeta, type SpecialCtx } from '../../op-meta.ts';
import { withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { passThroughSchema } from '../../schema/infer.ts';
import type { MaterializedDataset, Value } from '../../value.ts';
import { resolveDsArg } from './helpers.ts';

/**
 * `(limit ds N)` — keep the first `N` rows of `ds`. `N` must be a
 * non-negative integer.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - `N` negative / non-integer → schema-validation `ShapeError`.
 *
 * A `LazyDataset` input is appended as a `limit` plan node by the
 * dataset-op wrapper; materialised inputs run arquero's `.slice`
 * immediately. Schema is unchanged — row-cap doesn't add or drop
 * columns.
 */
export const limit = z
  .object({
    name: z.literal('limit'),
    params: z.tuple([astArg('<dataset>'), z.coerce.number().int().nonnegative()]),
  })
  .describe('Keep the first N rows. Composes via threading: `(-> ds (limit 5))`.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(-> wells (limit 10))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw, n] = data.params as readonly [Value, number];
      const concrete = await resolveDsArg(dsRaw, '(limit ds N)', ctx);
      return withTable(concrete, (t) => t.slice(0, n));
    },
    dataset: {
      // Row-cap only: columns and types unchanged.
      propagateSchema: passThroughSchema,
    },
  });
